import React from "react";
import { useParams, Link } from "react-router-dom";
import "animate.css";

import Navbar from "../Navbar/Navbar";
import MainBodyContainer from "../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../Footer/Footer";
import { CommunityProjectsData } from "../Data/CommunityProjectsData";

import "./CommunityProjects.css";

const CommunityProjectPage = () => {
  const { id } = useParams();
  const project = CommunityProjectsData.find((p) => p.id === id);

  if (!project) {
    return (
      <div>
        <Navbar />
        <MainBodyContainer heading={"Project Not Found"}>
          <div className="not-found">
            <h2>Project not found</h2>
            <p>The project you're looking for doesn't exist.</p>
            <Link to="/community-projects" className="back-link">
              ← Back to Community Projects
            </Link>
          </div>
        </MainBodyContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <MainBodyContainer heading={project.title}>
        {/* Hero Section */}
        <div className="project-detail-hero">
          <Link to="/community-projects" className="breadcrumb">
            ← Back to Community Projects
          </Link>
          <h1 className="animate__animated animate__fadeInUp">{project.title}</h1>
          <p className="hero-description">{project.description}</p>
        </div>

        {/* Who This Helps */}
        {project.whoThisHelps && (
          <div className="detail-section">
            <h2 className="section-header">Who This Project Supports</h2>
            <div className="who-helps-grid">
              {project.whoThisHelps.map((who, index) => (
                <div key={index} className="who-helps-item">
                  <span className="check-icon">✓</span>
                  <span>{who}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Provided */}
        {project.servicesProvided && (
          <div className="detail-section services-provided-section">
            <h2 className="section-header">Services We Provide</h2>
            <div className="services-provided-grid">
              {project.servicesProvided.map((item, index) => (
                <div key={index} className="service-provided-card">
                  <h3 className="service-name">{item.service}</h3>
                  <p className="service-description">{item.description}</p>
                  {item.casesSupported2024 && (
                    <p className="cases-stat">
                      <strong>{item.casesSupported2024}</strong> cases supported (2024)
                    </p>
                  )}
                  {item.outcomes && (
                    <p className="service-outcomes"><strong>Outcomes:</strong> {item.outcomes}</p>
                  )}
                  {item.delivery && <p className="service-delivery"><strong>Delivery:</strong> {item.delivery}</p>}
                  {item.participation2024 && (
                    <p className="participation-stat">
                      <strong>{item.participation2024}</strong> participants (2024)
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Core Services (for asylum seeker support) */}
        {project.coreServices && (
          <div className="detail-section services-provided-section">
            <h2 className="section-header">Core Services</h2>
            <div className="services-provided-grid">
              {project.coreServices.map((item, index) => (
                <div key={index} className="service-provided-card">
                  <h3 className="service-name">{item.service}</h3>
                  <p className="service-description">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total Impact */}
        {project.totalImpact2024 && (
          <div className="detail-section impact-section">
            <h2 className="section-header">Our Impact (2024-25)</h2>
            <div className="impact-grid">
              {project.totalImpact2024.casesSupported && (
                <div className="impact-item">
                  <span className="impact-value">{project.totalImpact2024.casesSupported}</span>
                  <span className="impact-label">Cases Supported</span>
                </div>
              )}
              {project.totalImpact2024.financialBenefitsSecured && (
                <div className="impact-item">
                  <span className="impact-value">{project.totalImpact2024.financialBenefitsSecured}</span>
                  <span className="impact-label">Benefits Secured</span>
                </div>
              )}
              {project.totalImpact2024.housingOutcomes && (
                <div className="impact-item">
                  <span className="impact-value">{project.totalImpact2024.housingOutcomes}</span>
                  <span className="impact-label">Housing Outcomes</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Impact Metrics */}
        {project.impactMetrics && (
          <div className="detail-section impact-section">
            <h2 className="section-header">Our Impact</h2>
            <div className="impact-grid">
              {Object.entries(project.impactMetrics).map(([key, value], index) => (
                <div key={index} className="impact-item">
                  <span className="impact-value">{value}</span>
                  <span className="impact-label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Case Study */}
        {project.caseStudy && (
          <div className="detail-section case-study-section">
            <h2 className="section-header">Case Study</h2>
            <div className="case-study-card">
              {project.caseStudy.situation && (
                <div className="case-section">
                  <h4>Situation</h4>
                  <p>{project.caseStudy.situation}</p>
                </div>
              )}
              {project.caseStudy.barriers && (
                <div className="case-section">
                  <h4>Barriers Faced</h4>
                  <p>{project.caseStudy.barriers}</p>
                </div>
              )}
              {project.caseStudy.intervention && (
                <div className="case-section">
                  <h4>Our Intervention</h4>
                  <p>{project.caseStudy.intervention}</p>
                </div>
              )}
              {project.caseStudy.outcome && (
                <div className="case-section">
                  <h4>Outcome</h4>
                  <p>{project.caseStudy.outcome}</p>
                </div>
              )}
              {project.caseStudy.quote && (
                <blockquote className="case-quote">
                  "{project.caseStudy.quote}"
                </blockquote>
              )}
            </div>
          </div>
        )}

        {/* Partner Organizations */}
        {project.partnerOrganizations && (
          <div className="detail-section partners-section">
            <h2 className="section-header">Our Partners</h2>
            <div className="partners-grid">
              {project.partnerOrganizations.map((partner, index) => (
                <div key={index} className="partner-item">
                  {partner}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Access Info */}
        {project.accessInfo && (
          <div className="detail-section access-section">
            <h2 className="section-header">How to Access Support</h2>
            <div className="access-grid">
              {project.accessInfo.cost && (
                <div className="access-item">
                  <span className="access-icon">💰</span>
                  <div>
                    <h4>Cost</h4>
                    <p>{project.accessInfo.cost}</p>
                  </div>
                </div>
              )}
              {project.accessInfo.languages && (
                <div className="access-item">
                  <span className="access-icon">🌍</span>
                  <div>
                    <h4>Languages</h4>
                    <p>{project.accessInfo.languages}</p>
                  </div>
                </div>
              )}
              {project.accessInfo.contactMethods && (
                <div className="access-item">
                  <span className="access-icon">📞</span>
                  <div>
                    <h4>Contact Methods</h4>
                    <p>{project.accessInfo.contactMethods}</p>
                  </div>
                </div>
              )}
              {project.accessInfo.urgentCases && (
                <div className="access-item">
                  <span className="access-icon">🚨</span>
                  <div>
                    <h4>Urgent Cases</h4>
                    <p>{project.accessInfo.urgentCases}</p>
                  </div>
                </div>
              )}
              {project.accessInfo.referrals && (
                <div className="access-item">
                  <span className="access-icon">🔗</span>
                  <div>
                    <h4>Referrals</h4>
                    <p>{project.accessInfo.referrals}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="detail-cta">
          <h2>Need Support?</h2>
          <p>Contact us to access this service. We're here to help.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="cta-button primary">
              Get Support Now
            </Link>
            <Link to="/community-projects" className="cta-button secondary">
              View All Projects
            </Link>
          </div>
        </div>
      </MainBodyContainer>
      <Footer />
    </div>
  );
};

export default CommunityProjectPage;
